import { useEffect, useRef, type ReactNode } from 'react'
import { ArrowLeft, BookHeart, BookOpen, Heart, Home, Library, Moon, Play, Rabbit, Search, Settings, Sparkles } from 'lucide-react'
import { storyAgeLabel, type Story } from '../data'
import { storyRenderMode } from '../lib/render-mode'

export const Icons = { ArrowLeft, BookHeart, BookOpen, Heart, Home, Library, Moon, Play, Rabbit, Search, Settings, Sparkles }

export function Brand() {
  return <span className="brand" aria-label="Lulla">lulla<span aria-hidden="true">✧</span></span>
}

export function ScreenHeader({ title, onBack, action }: { title: string; onBack?: () => void; action?: ReactNode }) {
  return <header className="screen-header">
    {onBack ? <button className="icon-button" onClick={onBack} aria-label="이전 화면"><ArrowLeft /></button> : <span className="icon-spacer" />}
    <span>{title}</span>
    {action ?? <span className="icon-spacer" />}
  </header>
}

export function PrimaryButton({ children, onClick, disabled, type = 'button', className = '' }: { children: ReactNode; onClick?: () => void; disabled?: boolean; type?: 'button' | 'submit'; className?: string }) {
  return <button type={type} className={`button button-primary ${className}`} onClick={onClick} disabled={disabled}>{children}</button>
}

export function SecondaryButton({ children, onClick, className = '', disabled }: { children: ReactNode; onClick?: () => void; className?: string; disabled?: boolean }) {
  return <button type="button" className={`button button-secondary ${className}`} onClick={onClick} disabled={disabled}>{children}</button>
}

export function TextButton({ children, onClick, className = '' }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return <button type="button" className={`text-button ${className}`} onClick={onClick}>{children}</button>
}

export function StepProgress({ current, labels }: { current: number; labels: string[] }) {
  return <div className="step-wrap" aria-label={`${labels.length}단계 중 ${current}단계: ${labels[current - 1]}`}>
    <div className="step-bar" aria-hidden="true">{labels.map((label, index) => <span key={label} className={index < current ? 'is-complete' : ''} />)}</div>
    <p>{labels[current - 1]}</p>
  </div>
}

export function StoryCover({ story, large = false }: { story: Story; large?: boolean }) {
  const art = story.coverUrl || (story.cover === 'flower' ? '/assets/flower-friends.webp' : story.cover === 'moon' ? '/assets/moon-bunny.webp' : undefined)
  return <div className={`story-cover story-${story.cover} ${large ? 'story-cover-large' : ''}`}>
    {art ? <img src={art} alt={large ? `${story.title} 표지 그림` : ''} /> : <div className="generated-cover" aria-label={large ? `${story.title} 장식 표지` : undefined} role={large ? 'img' : undefined}>
      <span className="cover-moon" /><span className="cover-cloud one" /><span className="cover-cloud two" /><span className="cover-star">✦</span>
    </div>}
    <span className="book-spine" aria-hidden="true" />
  </div>
}

export function StoryCard({ story, onSelect }: { story: Story; onSelect: () => void }) {
  return <button type="button" className="story-card" onClick={onSelect} aria-label={`${story.title}, ${story.minutes}분, ${story.mood}`}>
    <StoryCover story={story} />
    <strong>{story.title}</strong>
    {story.sourceKind === 'audio' && <span className="story-kind">원본 듣기 · {storyRenderMode(story) === 'renarration' ? story.conversionAvailable === true ? '부모 목소리로 다시 읽기' : '다시 읽기 준비 중' : story.conversionStatus === 'experimental_unverified' ? '실험 변환 · 품질 미검증' : story.conversionAvailable === true ? '부모 목소리 변환' : story.conversionStatus === 'rights_unconfirmed' ? '변환 허가 미확인' : story.conversionStatus === 'quality_unverified' || story.conversionStatus === 'quality_failed' ? '변환 품질 점검 중' : '변환 준비 중'}</span>}
    <small>{story.minutes}분 · {storyAgeLabel(story)} · {story.mood}</small>
  </button>
}

export type MainTab = 'home' | 'stories' | 'library' | 'family'
export function BottomNav({ active, navigate }: { active: MainTab; navigate: (tab: MainTab) => void }) {
  const items: { id: MainTab; label: string; Icon: typeof Home }[] = [
    { id: 'home', label: '홈', Icon: Home }, { id: 'stories', label: '동화숲', Icon: BookOpen },
    { id: 'library', label: '우리 책장', Icon: Library }, { id: 'family', label: '가족', Icon: Heart }
  ]
  return <nav className="bottom-nav" aria-label="주 메뉴">{items.map(({ id, label, Icon }) => <button key={id} onClick={() => navigate(id)} aria-current={active === id ? 'page' : undefined}><Icon /><span>{label}</span></button>)}</nav>
}

export function Notice({ children, tone = 'info', role }: { children: ReactNode; tone?: 'info' | 'success' | 'warning' | 'error'; role?: 'status' | 'alert' }) {
  return <div className={`notice notice-${tone}`} role={role}>{children}</div>
}

export function EmptyState({ icon, title, body, action }: { icon: ReactNode; title: string; body: string; action: ReactNode }) {
  return <section className="empty-state"><div className="empty-icon">{icon}</div><h2>{title}</h2><p>{body}</p>{action}</section>
}

export function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel, destructive = false, busy = false }: { title: string; body: string; confirmLabel: string; onConfirm: () => void; onCancel: () => void; destructive?: boolean; busy?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const invoker = useRef<HTMLElement | null>(null)
  useEffect(() => {
    invoker.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    ref.current?.showModal()
    cancelRef.current?.focus()
    return () => {
      if (ref.current?.open) ref.current.close()
      requestAnimationFrame(() => invoker.current?.focus())
    }
  }, [])
  return <dialog ref={ref} className="confirm-dialog" aria-labelledby="dialog-title" aria-describedby="dialog-body" aria-busy={busy || undefined} onCancel={event => { event.preventDefault(); if (!busy) onCancel() }}>
      <h2 id="dialog-title">{title}</h2><p id="dialog-body">{body}</p>
      <div className="dialog-actions"><button ref={cancelRef} type="button" className="button button-secondary" onClick={onCancel} disabled={busy}>취소</button><button type="button" className={`button ${destructive ? 'button-danger' : 'button-primary'}`} onClick={onConfirm} disabled={busy}>{confirmLabel}</button></div>
  </dialog>
}
